import { type Action } from 'react-sweet-state';

import type { UnconnectedApp } from '../../ui/chat-prompt-input/app-connection-banner';

import type { AppConnectionBannerState, ShowBannerOptions } from './types';

export const actions = {
	/**
	 * Show the app connection banner with the given unconnected apps
	 */
	showBanner:
		(
			unconnectedApps: UnconnectedApp[],
			options?: ShowBannerOptions,
		): Action<AppConnectionBannerState> =>
		({ setState }) => {
			if (unconnectedApps.length > 0) {
				setState({
					unconnectedApps,
					isVisible: true,
					forceMultiline: options?.forceMultiline ?? false,
					showConnectedState: options?.showConnectedState ?? false,
				});
			}
		},

	/**
	 * Hide the banner and clear the unconnected apps
	 */
	hideBanner:
		(): Action<AppConnectionBannerState> =>
		({ setState }) => {
			setState({
				unconnectedApps: [],
				isVisible: false,
				forceMultiline: false,
				showConnectedState: false,
			});
		},

	/**
	 * Handle a successful connection.
	 *
	 * Default behavior removes the app's row (and hides the banner once the last
	 * one connects). When `showConnectedState` is on, the row is kept instead —
	 * the connected state is derived from the connectors store (`isUserOAuthed`,
	 * flipped by the shared OAuth flow), so there is nothing to update here.
	 */
	markAppAsConnected:
		(appSource: string): Action<AppConnectionBannerState> =>
		({ setState, getState }) => {
			const { unconnectedApps, showConnectedState } = getState();

			if (showConnectedState) {
				return;
			}

			const updatedApps = unconnectedApps.filter((app) => app.source !== appSource);

			if (updatedApps.length === 0) {
				// All apps connected, hide the banner
				setState({
					unconnectedApps: [],
					isVisible: false,
					forceMultiline: false,
				});
			} else {
				setState({
					unconnectedApps: updatedApps,
				});
			}
		},
};
